import React, { useEffect, useState } from 'react';
import { RNLLoader, Btn } from '../UI';
import { errorText } from '../../utils';
import * as api from '../../api';
import PaymentVoucherForm from './PaymentVoucherForm';

/**
 * Turns an address into the voucher form.
 *
 * The form is handed whole objects — a loaded requisition, an existing
 * voucher, or the chosen payment method — and reads every one of them
 * SYNCHRONOUSLY in its useState initialisers. A url carries ids, not objects.
 *
 * Rather than restructure the screen where vouchers are created and edited,
 * this resolves the ids first and mounts the form only once the object exists.
 * The form's initialisers then run exactly as they do when a button opened it,
 * so both routes in produce an identically configured screen — which is the
 * property that matters, and the one that quietly breaks when a deep link gets
 * its own loading path.
 */
export default function PaymentVoucherFormRoute({
  voucherId, requisitionId, methodId, onBack, onSaved, toast,
}) {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState({ loading: true });
      try {
        // Exactly one of these is set by the route; whichever it is becomes
        // the prop the form already expects.
        if (voucherId) {
          const v = await api.getPaymentVoucher(voucherId);
          if (!cancelled) setState({ loading: false, existingVoucher: v?.data || v });
        } else if (requisitionId) {
          const r = await api.getRequisitionForVoucherLoad(requisitionId);
          if (!cancelled) setState({ loading: false, initialRequisition: r?.data || r });
        } else if (methodId) {
          const m = await api.getPaymentVoucherMethod(methodId);
          if (!cancelled) setState({ loading: false, voucherMethod: m?.data || m });
        } else {
          // A bare /new with no method: the form handles that already.
          if (!cancelled) setState({ loading: false });
        }
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: errorText(e) });
      }
    })();
    return () => { cancelled = true; };
  }, [voucherId, requisitionId, methodId]);

  if (state.loading) return <RNLLoader title="Loading voucher…" subtitle="Fetching the details" />;

  if (state.error) {
    // A stale or mistyped link, or a voucher somebody deleted. Say so and
    // offer the way back rather than mounting a form with nothing in it.
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <div style={{ color: 'var(--red)', fontSize: 13.5, marginBottom: 16 }}>{state.error}</div>
        <Btn variant="outline" onClick={onBack}>Back</Btn>
      </div>
    );
  }

  return (
    <PaymentVoucherForm
      toast={toast}
      initialRequisition={state.initialRequisition}
      existingVoucher={state.existingVoucher}
      voucherMethod={state.voucherMethod}
      onBack={onBack}
      onSaved={onSaved}
    />
  );
}
